import { isPlaceholderLocationText } from '@/utils/locationUtils';
import {
  MAX_TEMPLATE_TAGS,
  MIN_TEMPLATE_DESCRIPTION_LENGTH,
  MIN_TEMPLATE_LEARNINGS_LENGTH,
} from './templateOptions';
import type { PublishDestination, StepId } from './types';

export type FieldKey =
  | 'storyName'
  | 'creatorName'
  | 'projectName'
  | 'projectDescription'
  | 'projectTags'
  | 'projectImages'
  | 'anchorImage'
  | 'anchorAddress'
  | 'templateName'
  | 'templateDescription'
  | 'templateLearnings'
  | 'templateDifficulty'
  | 'templateTags'
  | 'templateFeatures'
  | 'templateVideo'
  | 'templateCover';

export interface PublishFlowValues {
  storyName: string;
  creatorName: string;
  projectName: string;
  projectDescription: string;
  projectTags: string[];
  hasProjectPortrait: boolean;
  hasProjectLandscape: boolean;
  hasAnchorImage: boolean;
  // True when the anchor image is still the auto-populated Immersal scan thumbnail,
  // which the creator must replace with their own before publishing.
  anchorThumbnailUnchanged: boolean;
  anchorAddress: string;
  templateName: string;
  templateDescription: string;
  templateLearnings: string;
  templateDifficulty: string;
  templateTags: string[];
  templateFeatures: string[];
  hasTemplateVideo: boolean;
  hasTemplateCover: boolean;
}

// Matches the auto-generated scene name (e.g. "Story 1") plus the bare "Story"
// placeholder, mirroring the publish validation in utils/publishValidation.ts.
// Creators must rename before publishing.
const DEFAULT_STORY_NAME_RE = /^Story(\s+\d+)?$/i;

const MIN_NAME_LENGTH = 3;

export function isDefaultStoryName(name: string): boolean {
  return DEFAULT_STORY_NAME_RE.test(name.trim());
}

export function isValidStoryName(name: string): boolean {
  return name.trim().length > MIN_NAME_LENGTH && !isDefaultStoryName(name);
}

export function storyNameError(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return FIELD_ERROR_MESSAGES.storyName;
  if (trimmed.length <= MIN_NAME_LENGTH) return 'Story name must be more than 3 characters';
  return 'Choose a name other than the default "Story Number"';
}

// Matches the auto-generated project name (e.g. "Untitled project 4") created in
// Dashboard.tsx. Creators must rename before publishing.
const DEFAULT_PROJECT_NAME_RE = /^untitled project\s+\d+$/i;

export function isDefaultProjectName(name: string): boolean {
  return DEFAULT_PROJECT_NAME_RE.test(name.trim());
}

export function isValidProjectName(name: string): boolean {
  return name.trim().length > MIN_NAME_LENGTH && !isDefaultProjectName(name);
}

export function projectNameError(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return FIELD_ERROR_MESSAGES.projectName;
  if (trimmed.length <= MIN_NAME_LENGTH) return 'Project name must be more than 3 characters';
  return 'Please change your project name from the default "Untitled project"';
}

const REQUIRED_BY_STEP: Record<StepId, FieldKey[]> = {
  'choose-platform': ['storyName', 'creatorName'],
  'project-info': ['projectName', 'projectDescription', 'projectTags', 'projectImages'],
  'scene-info': [],
  'anchor-info': ['anchorImage', 'anchorAddress'],
  confirmation: [],
  'template-info': ['templateName', 'templateDescription', 'templateLearnings', 'templateDifficulty'],
  'template-features': ['templateFeatures', 'templateTags'],
  'template-media': ['templateVideo', 'templateCover'],
  'template-confirm': [],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isEmailLike(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function creatorNameError(name: string): string {
  if (name.trim().length === 0) return 'Creator name is required';
  if (isEmailLike(name)) return "Creator name can't be an email — pick a display name";
  return '';
}

export const FIELD_ERROR_MESSAGES: Record<FieldKey, string> = {
  storyName: 'Story name is required',
  creatorName: "Creator name can't be an email — pick a display name",
  projectName: 'Project name is required',
  projectDescription: 'Description is required',
  projectTags: 'Add at least one tag',
  projectImages: 'Add a portrait and landscape cover image',
  anchorImage: 'An anchor image is required',
  anchorAddress: 'Enter a real address',
  templateName: 'Template name is required',
  templateDescription: `Write at least ${MIN_TEMPLATE_DESCRIPTION_LENGTH} characters`,
  templateLearnings: `Write at least ${MIN_TEMPLATE_LEARNINGS_LENGTH} characters`,
  templateDifficulty: 'Pick a difficulty',
  templateTags: `Add 1 to ${MAX_TEMPLATE_TAGS} tags`,
  templateFeatures: 'Tick at least one feature',
  templateVideo: 'A video is required',
  templateCover: 'A cover image is required',
};

function isFieldFilled(key: FieldKey, v: PublishFlowValues): boolean {
  switch (key) {
    case 'storyName':
      return isValidStoryName(v.storyName);
    case 'creatorName':
      return v.creatorName.trim().length > 0 && !isEmailLike(v.creatorName);
    case 'projectName':
      return isValidProjectName(v.projectName);
    case 'projectDescription':
      return v.projectDescription.trim().length > 0;
    case 'projectTags':
      return v.projectTags.length > 0;
    case 'projectImages':
      return v.hasProjectPortrait && v.hasProjectLandscape;
    case 'anchorImage':
      return v.hasAnchorImage && !v.anchorThumbnailUnchanged;
    // Blank stays acceptable (the location is optional for image anchors); only the
    // normalised "null" placeholder is rejected, so this can never trap a creator
    // who legitimately left the address empty.
    case 'anchorAddress':
      return !isPlaceholderLocationText(v.anchorAddress);
    case 'templateName':
      return v.templateName.trim().length > 0;
    case 'templateDescription':
      return v.templateDescription.trim().length >= MIN_TEMPLATE_DESCRIPTION_LENGTH;
    case 'templateLearnings':
      return v.templateLearnings.trim().length >= MIN_TEMPLATE_LEARNINGS_LENGTH;
    case 'templateDifficulty':
      return v.templateDifficulty.trim().length > 0;
    case 'templateTags':
      return v.templateTags.length > 0 && v.templateTags.length <= MAX_TEMPLATE_TAGS;
    case 'templateFeatures':
      return v.templateFeatures.length > 0;
    case 'templateVideo':
      return v.hasTemplateVideo;
    case 'templateCover':
      return v.hasTemplateCover;
  }
}

// The template path locks the story name field, so requiring it there would trap a
// creator whose story still carries its default name. The template copy is named
// from templateName, which template-info requires in its place.
export function getMissingFields(
  step: StepId,
  values: PublishFlowValues,
  destination: PublishDestination = 'audience',
): FieldKey[] {
  const required =
    destination === 'template'
      ? REQUIRED_BY_STEP[step].filter((key) => key !== 'storyName')
      : REQUIRED_BY_STEP[step];
  return required.filter((key) => !isFieldFilled(key, values));
}

export function withoutKeys(set: Set<FieldKey>, ...keys: FieldKey[]): Set<FieldKey> {
  if (!keys.some((k) => set.has(k))) return set;
  const next = new Set(set);
  keys.forEach((k) => next.delete(k));
  return next;
}

export const ERROR_INPUT_CLASS = 'border-lureo-danger-ink';
